
"use client"

import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type { SleepPeriod } from "@/lib/types";
import { subDays, format, startOfDay, endOfDay } from "date-fns";

type SleepChartProps = {
  periods: SleepPeriod[];
}

// Helper to calculate sleep durations in hours
const calculateSleepDuration = (period: SleepPeriod) => {
  // Validate timestamps
  if (!period.startTime || !period.endTime) {
    console.warn('Invalid sleep period timestamps:', period);
    return 0;
  }
  
  let sleepMillis = period.endTime.getTime() - period.startTime.getTime();
  
  // Validate that end time is after start time
  if (sleepMillis < 0) {
    console.warn('Invalid sleep period: end time before start time:', period);
    return 0;
  }
  
  // Cap extremely long sleep periods (more than 48 hours seems unreasonable)
  if (sleepMillis > 48 * 60 * 60 * 1000) {
    console.warn('Extremely long sleep period detected:', period, 'Duration:', sleepMillis / (1000 * 60 * 60), 'hours');
    return 24; // Cap at 24 hours
  }
  
  if (period.type === 'night' && period.wakeUps) {
    const wakeUpMillis = period.wakeUps.reduce((total, wakeUp) => total + wakeUp.duration * 60 * 1000, 0);
    sleepMillis -= wakeUpMillis;
  }

  return sleepMillis / (1000 * 60 * 60);
};

const getNightWakeDuration = (period: SleepPeriod) => {
    if (period.type !== 'night' || !period.wakeUps) return 0;
    const wakeUpMillis = period.wakeUps.reduce((total, wakeUp) => total + wakeUp.duration * 60 * 1000, 0);
    return wakeUpMillis / (1000 * 60 * 60);
}

export function SleepChart({ periods }: SleepChartProps) {

  const last7DaysData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), i);
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);

    const periodsForDay = periods.filter(p => p.startTime >= dayStart && p.startTime <= dayEnd);
    
    const nightSleep = periodsForDay
      .filter(p => p.type === 'night')
      .reduce((total, period) => total + calculateSleepDuration(period), 0);

    const napSleep = periodsForDay
      .filter(p => p.type === 'nap')
      .reduce((total, period) => total + calculateSleepDuration(period), 0);
      
    const nightAwake = periodsForDay
      .filter(p => p.type === 'night')
      .reduce((total, period) => total + getNightWakeDuration(period), 0);

    const hasLogsForDay = periodsForDay.length > 0;
    const totalSleep = nightSleep + napSleep;
    // Only calculate awake hours if sleep data was logged for this day; otherwise leave 0 to avoid false "24h awake" bars
    const awake = hasLogsForDay ? Math.max(0, 24 - totalSleep - nightAwake) : 0;

    // Debug logging to identify extreme values
    if (nightSleep > 24 || napSleep > 24 || nightAwake > 24 || awake > 24) {
      console.warn('Extreme sleep values detected:', {
        date: format(date, "EEE"),
        nightSleep,
        napSleep,
        nightAwake,
        awake,
        totalSleep
      });
    }

    return {
      date: format(date, "EEE"),
      nightSleep: Number(Math.max(0, Math.min(24, nightSleep)).toFixed(2)),
      napSleep: Number(Math.max(0, Math.min(24, napSleep)).toFixed(2)),
      nightAwake: Number(Math.max(0, Math.min(24, nightAwake)).toFixed(2)),
      awake: Number(Math.max(0, Math.min(24, awake)).toFixed(2)),
      hasLogs: hasLogsForDay,
    };
  }).reverse();

  const chartConfig = {
    nightSleep: {
      label: "Night Sleep",
      color: "hsl(var(--chart-1))",
    },
    napSleep: {
      label: "Day Naps",
      color: "hsl(var(--chart-2))",
    },
    nightAwake: {
      label: "Night Awake",
      color: "hsl(var(--chart-4))",
    },
    awake: {
      label: "Awake",
      color: "hsl(var(--muted))",
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekly Sleep Summary</CardTitle>
        <CardDescription className="text-muted-foreground">Total time breakdown over the last 7 days.</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="aspect-auto h-[280px] w-full">
          <BarChart data={last7DaysData} accessibilityLayer stackOffset="sign" barGap={4}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis
              dataKey="date"
              tickLine={false}
              tickMargin={12}
              axisLine={false}
              fontSize={13}
              fontWeight={500}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              unit="h"
              fontSize={13}
              fontWeight={500}
              domain={[0, 24]}
              allowDataOverflow={false}
              scale="linear"
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))', opacity: 0.2 }}
              content={<ChartTooltipContent indicator="dot" />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar dataKey="nightSleep" fill="var(--color-nightSleep)" stackId="a" radius={0} />
            <Bar dataKey="napSleep" fill="var(--color-napSleep)" stackId="a" radius={0} />
            <Bar dataKey="nightAwake" fill="var(--color-nightAwake)" stackId="a" radius={0} />
            <Bar dataKey="awake" fill="var(--color-awake)" stackId="a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
